// Экран объяснения темы: четыре раздела и выход в задачи по ней.
//
// **Зачем он вообще, если разбор есть у каждой задачи.** Разбор отвечает на
// «почему здесь получилось так» и приходит после ошибки. Вопрос «как это
// вообще делается» он не закрывает: ученик, который не знает правила, соберёт
// его из четырёх разборов подряд, и соберёт неверно. Стоп-лист до июня 2027
// запрещает «красивые объяснения и видео» — текстовая страница на четыре
// минуты чтения не они: ни видео, ни картинок-иллюстраций, ни второй страницы
// у темы.
//
// **Страница кончается кнопкой, а не «удачи».** Прочитанное без применения
// забывается быстрее, чем читается; все выходы со страницы ведут в задачи по
// той же теме — в набор дня, в тренажёр или в зачёт. Исключение — вход с
// недорешённой задачи: там выход ведёт обратно к ней, и это то же правило, а
// не другое.

import { useEffect, useRef, useState } from "react";
import type { TheorySection } from "../../convex/lib/unclear";
import { api, useMutation, useQuery, type TheoryPage } from "../api";
import { reasonOf } from "../lib/errors";
import { TheoryBody } from "../ui/TheoryBody";
import { UnclearButton, UnclearPanel } from "../ui/Unclear";

const SECTIONS: { key: TheorySection; label: string }[] = [
  { key: "why", label: "Зачем это нужно" },
  { key: "rule", label: "Правило" },
  { key: "example", label: "Разобранный пример" },
  { key: "mistake", label: "Где теряют баллы" },
];

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="theory__section">
      <div className="card__label">{label}</div>
      {children}
    </section>
  );
}

/**
 * «Не совсем понятно» под разделами (docs/24-unclear.md): та же тихая иконка,
 * что у разбора задачи, и то же окошко — разделы вместо номеров шагов.
 * «Зачем это нужно» в выборе нет: мотивация бывает неубедительной, но не
 * непонятной, и чинить по такой отметке нечего.
 */
const MARKABLE = SECTIONS.filter((s) => s.key !== "why");
function TheoryUnclear({ learnerKey, skillId }: { learnerKey: string; skillId: string }) {
  const marks = useQuery(api.unclear.forTheory, { learnerKey, skillId });
  const setSections = useMutation(api.unclear.setSections);
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="solution__tools">
        <UnclearButton
          open={open}
          marked={(marks?.sections.length ?? 0) > 0}
          onClick={() => setOpen((o) => !o && marks !== undefined)}
        />
      </div>
      {open && marks !== undefined ? (
        <UnclearPanel
          prompt="Какие разделы непонятны"
          options={MARKABLE}
          saved={marks.sections}
          savedComment={marks.comment}
          onSave={(sections, comment) => setSections({ learnerKey, skillId, sections, comment })}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

export function Theory({
  learnerKey,
  skillId,
  open,
  onPractice,
  onDrill,
  onTestOut,
  onToday,
  returnLabel = "К набору дня",
  backToItemKey = null,
  onBackToItem,
}: {
  learnerKey: string;
  skillId: string;
  /** Открыта ли тема на карте. От этого зависит, чем страница кончается. */
  open: boolean;
  /** «Решать эту тему» — та же порция из трёх задач, что в карточке набора. */
  onPractice: (skillId: string) => void;
  /** Тренажёр: задачи по теме подряд, с разбором. Есть у любой темы. */
  onDrill: (skillId: string) => void;
  /** Зачёт у закрытой темы: три задачи посложнее, без подсказок. */
  onTestOut: (skillId: string) => void;
  onToday: () => void;
  returnLabel?: string;
  /**
   * Откуда пришли. `null` — с карты тем; строка — недорешённая задача, и
   * тогда страница кончается возвратом к ней.
   */
  backToItemKey?: string | null;
  onBackToItem?: (itemKey: string) => void;
}) {
  const openPage = useMutation(api.theory.open);
  const [page, setPage] = useState<TheoryPage | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // Ref, а не пустой список зависимостей: StrictMode прогоняет эффект дважды,
  // и в `theoryViews` на каждое открытие ложились бы две строки. Тот же приём,
  // что у снимка на «Сегодня», и та же причина — счётчик должен считать
  // открытия, а не перерисовки.
  const asked = useRef<string | null>(null);
  /** `null` — пришли не с задачи. Иначе — что делать по «вернуться к задаче». */
  const returning =
    backToItemKey !== null && onBackToItem !== undefined
      ? () => onBackToItem(backToItemKey)
      : null;
  useEffect(() => {
    if (asked.current === skillId) return;
    asked.current = skillId;
    void openPage({ learnerKey, skillId })
      .then(setPage)
      .catch((e: unknown) => setFailed(reasonOf(e)));
  }, [learnerKey, skillId, openPage]);

  if (failed !== null) {
    return (
      <div className="stack">
        <div className="notice">{failed}</div>
        <div className="row row--actions">
          {returning !== null ? (
            <button className="btn" onClick={returning}>
              Вернуться к задаче
            </button>
          ) : null}
          <button className="btn" onClick={onToday}>
            {returnLabel}
          </button>
        </div>
      </div>
    );
  }

  if (page === null) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 320, height: 34 }} />
        <div className="skeleton" />
        <div className="skeleton" style={{ width: 260 }} />
      </div>
    );
  }

  return (
    <div className="theory">
      {/* «Разбор темы» стоит в шапке приложения; здесь — название темы. */}
      <h1 className="h1">{page.title}</h1>

      {SECTIONS.map((sec) => (
        <Section key={sec.key} label={sec.label}>
          <TheoryBody source={page[sec.key]} />
        </Section>
      ))}
      <TheoryUnclear learnerKey={learnerKey} skillId={skillId} />

      {/*
        Концовка у страницы разная, и различает её не текст, а состояние темы
        на карте. Открытая тема кончается задачами по ней. Закрытая кончается
        зачётом — потому что «решать» по ней система откажет, и кнопка, которая
        объясняет отказ после нажатия, хуже отсутствующей.

        Закрытая тема при этом — обычное состояние для прочитанного разбора:
        школьная программа идёт своим порядком, а карта своим, и читают правило
        как раз до того, как планировщик до темы дошёл.

        **Тренажёр стоит у обеих, и у закрытой он первый.** До 16 сентября
        2026 у закрытой темы кнопка была одна — «Сдать зачёт», то есть экзамен
        сразу после правила. Ученик, который тему только что разобрал и хочет
        её порешать, упирался в измерение вместо тренировки; ровно на это
        пожаловались на теореме Виета. Тренажёр ничего не открывает и ничего не
        отнимает, поэтому он и может стоять у любой темы — разбор в
        `convex/drill.ts`.
      */}
      {/*
        Пришли с недорешённой задачи — концовка одна: назад к ней. Ни «решать
        эту тему», ни зачёта здесь нет намеренно: обе кнопки начинают новую
        порцию задач и бросают ту, ради которой ученик сюда и пришёл.
      */}
      {returning !== null ? (
        <div className="row row--actions" style={{ marginTop: 24 }}>
          <button className="btn btn--primary btn--big" onClick={returning}>
            Вернуться к задаче
          </button>
          <button className="btn btn--big" onClick={onToday}>
            {returnLabel}
          </button>
        </div>
      ) : (
        <>
          {open ? null : (
            <p className="theory__p" style={{ marginTop: 24 }}>
              Эта тема на карте ещё закрыта: система не видела, что предпосылки
              пройдены. Прочитанное правило — половина; вторая половина в том, чтобы
              показать его на задачах. Тренажёр — задачи по теме подряд, с разбором
              после каждой; он ничего не открывает и ничего не отнимает. Зачёт —
              три задачи посложнее, без подсказок; сдашь — тема откроется и встанет
              в набор.
            </p>
          )}

          <div className="row row--actions" style={{ marginTop: open ? 24 : 14 }}>
            {/*
              Тренажёр — главная кнопка у закрытой темы и вторая у открытой.
              У открытой первой остаётся добавка в набор: там задачи идут в счёт
              дня и двигают владение, и это сильнее тренировки, которая не в
              счёт. У закрытой в счёт не идёт ничто, кроме зачёта, — значит
              выбирать ученику между «порешать» и «сдать», и порешать первее.
            */}
            {open ? (
              <button className="btn btn--primary btn--big" onClick={() => onPractice(page.skillId)}>
                Решать эту тему
              </button>
            ) : (
              <button className="btn btn--primary btn--big" onClick={() => onDrill(page.skillId)}>
                Пройти тему в тренажёре
              </button>
            )}
            {open ? (
              <button className="btn btn--big" onClick={() => onDrill(page.skillId)}>
                Тренажёр
              </button>
            ) : (
              <button className="btn btn--big" onClick={() => onTestOut(page.skillId)}>
                Сдать зачёт по теме
              </button>
            )}
            <button className="btn btn--big" onClick={onToday}>
              {returnLabel}
            </button>
          </div>
          {/*
            Чем эти кнопки различаются, сказано один раз и числами, а не
            обещаниями: одна идёт в счёт дня, другая нет.
          */}
          <p className="theory__p faint" style={{ marginTop: 12 }}>
            {open
              ? "«Решать эту тему» — три задачи сверх набора дня: они идут в счёт дня и двигают владение. Тренажёр не идёт в счёт и не ограничен ничем, кроме задач в теме."
              : "Тренажёр не идёт в счёт дня и владения не двигает: он для того, чтобы разобраться. Открывает тему только зачёт."}
          </p>
        </>
      )}
    </div>
  );
}
